import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Internal components
import Correlated from '../../../components/Correlated/Correlated'
import HintBlock from '../../../components/HintBlock/HintBlock'
import LayoutProgram from '../../../components/LayoutProgram/LayoutProgram'
import PageHeader from '../../../components/PageHeader/PageHeader'
import PageSummary from '../../../components/PageSummary/PageSummary'
import Slideshow from '../../../components/Slideshow/Slideshow'
import TrainingBlock from '../../../components/TrainingBlock/TrainingBlock'

// Utils
import getHintBlockData from '../../../utils/data/getHintBlockData'
import getProgramPropertyData from '../../../utils/data/getProgramPropertyData'
import getRelatedSearchUrl from '../../../utils/data/getRelatedSearchUrl'
import getSession from '../../../utils/auth/getSession'
import getSiteHandle from '../../../utils/i18n/getSiteHandle'
import queryServerSideData from '../../../utils/data/queryServerSideData'

// Context
import { PrintContext } from '../../../context/PrintContext'
import { UserContext } from '../../../context/UserContext'

const ProgramDetailPage = (props) => {
  const { site, entry, correlated } = props
  if (!entry) return false

  // Context
  const printContext = useContext(PrintContext)
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // Prepare data for component
  const containsTrainings = (entry?.trainings?.length || entry?.userTrainingsAndTrainings?.length) > 0
  const propertyData = getProgramPropertyData(entry, t, 'programDetail')
  const frequencyCount = entry.timesPerWeekCategory[0]?.title
    ? parseInt(entry.timesPerWeekCategory[0].title.charAt(0))
    : 1
  const relatedSearchUrl = getRelatedSearchUrl(entry, 0, 15, site, 'programs')

  const hintBlockEmptyTrainingData = !containsTrainings ? getHintBlockData('emptyTrainings') : false
  const hintBlockUserProgramData =
    containsTrainings && entry.typeHandle === 'userProgram' ? getHintBlockData('userProgram') : false

  const [slideshowCardsState, setSlideshowCardsState] = useState([])
  const [programPurposesState, setProgramPurposesState] = useState(false)
  const [programTrainingsState, setProgramTrainingsState] = useState(
    entry.typeHandle === 'userProgram' ? entry.userTrainingsAndTrainings : entry.trainings
  )

  useEffect(() => {
    fetchSlideshowCards()
    setProgramTrainingsState(entry.typeHandle === 'userProgram' ? entry.userTrainingsAndTrainings : entry.trainings)
  }, [entry])

  useEffect(() => {
    printContext.setCurrentPage('program')
    return () => printContext.setCurrentPage(null)
  }, [])

  useEffect(() => {
    if (printContext.fetchPrintData) fetchProgramPrintData()
  }, [printContext.fetchPrintData])

  const fetchProgramPrintData = async () => {
    printContext.setShowFallBack(true)
    const printData = await fetch(`/api/knvb/print?id=${parseInt(entry.id)}&type=program`)
    const printJson = await printData.json()

    if (!printJson) return false
    setProgramTrainingsState(printJson.trainings)
    setProgramPurposesState(printJson.purposes)

    setTimeout(() => {
      printContext.setShowFallBack(false)
      printContext.setPrintDataFetched(true)
    }, 2000)
  }

  const fetchSlideshowCards = async () => {
    if (!relatedSearchUrl) return false
    const searchsUrl = relatedSearchUrl
    const searchResponse = await fetch(`/api/knvb/search?url=${encodeURIComponent(searchsUrl)}&site=${site}`)
    const searchResults = await searchResponse.json()
    setSlideshowCardsState(searchResults?.results ? [...searchResults.results] : [])
  }

  return (
    <>
      <LayoutProgram
        headerContent={
          <>
            <PageHeader entry={entry} type="program" propertyData={propertyData} showSimplified={!containsTrainings} />
            {containsTrainings && <PageSummary entry={entry} type="program" />}
          </>
        }
        mainContent={
          <>
            {!containsTrainings && hintBlockEmptyTrainingData && entry.typeHandle === 'userProgram' && (
              <HintBlock {...hintBlockEmptyTrainingData} />
            )}
            {containsTrainings &&
              programTrainingsState.map((training, index) => {
                const purposes = programPurposesState
                  ? programPurposesState.filter((purpose) => {
                      return purpose.id === parseInt(training.id)
                    })
                  : false
                return (
                  <TrainingBlock
                    entry={training}
                    purposes={purposes}
                    frequency={frequencyCount}
                    index={index}
                    key={`TrainingBlock--${index}`}
                  />
                )
              })}
            {containsTrainings && hintBlockUserProgramData && entry.typeHandle === 'userProgram' && (
              <HintBlock {...hintBlockUserProgramData} />
            )}
          </>
        }
      />
      {slideshowCardsState && containsTrainings && (
        <Slideshow title={t('SLIDESHOW__RELATED_PROGRAMS')} cards={slideshowCardsState} id={entry.id} />
      )}
      {correlated && correlated.correlatedPrograms[0] && <Correlated {...correlated.correlatedPrograms[0]} />}
    </>
  )
}

export async function getServerSideProps({ params, req, res, locale }) {
  const session = await getSession(req, res)
  if (session.error) {
    return {
      props: {
        error: true,
        errorCode: session.status,
        message: session.message,
        ...(await serverSideTranslations(
          locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
          process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
        ))
      }
    }
  }
  const siteHandle = getSiteHandle(locale)

  // avoid fetching when params.id is not an integer
  if (!parseInt(params.id)) {
    return {
      notFound: true
    }
  }

  const globalJson = await queryServerSideData(
    'qGlobal.graphql',
    { site: siteHandle, seoUri: `/program/id/${parseInt(params.id)}`, seoSite: siteHandle },
    session.cookieString
  )

  const programDetailJson = await queryServerSideData(
    'qProgramDetail.graphql',
    { id: params.id, site: siteHandle },
    session.cookieString
  )

  if (programDetailJson.data?.entry === null)
    return {
      notFound: true
    }

  return {
    props: {
      site: siteHandle,
      ...(await serverSideTranslations(
        locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
        process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
      )),
      ...globalJson.data,
      ...programDetailJson.data
    }
  }
}

ProgramDetailPage.propTypes = {
  site: PropTypes.string,
  entry: PropTypes.object,
  correlated: PropTypes.object
}

export default ProgramDetailPage
